import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <PageHeader 
        title="Privacy Policy"
      />
      
      <main className="flex-grow pb-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl pt-12">
          <div className="bg-white p-8 rounded-lg shadow-sm space-y-8">
            <div>
              <h1 className="text-2xl font-bold mb-2">EMU Mentoring Pty Ltd Privacy Policy</h1>
              <div className="text-sm text-gray-500 mb-6">
                Last updated: 25/08/2025
              </div>
              <p className="text-gray-700 mb-4">
                EMU Mentoring Pty Ltd ("we", "us", or "our") is committed to protecting your privacy and handling your personal information in an open and transparent manner. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform and services, including our website and mobile applications (collectively, the "Services").
              </p>
              <p className="text-gray-700">
                We comply with the Australian Privacy Principles (APPs) under the Privacy Act 1988 (Cth).
              </p>
            </div>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">1. What information we collect</h2>
              <p className="text-gray-700 mb-4">We collect personal information that is reasonably necessary for our functions and activities, including:</p>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
                <li>
                  <span className="font-medium">Identity and contact information:</span> Name, email address, profile photo, photo identification, and account information.
                </li>
                <li>
                  <span className="font-medium">Professional and biographical information:</span> Current resume, accreditations, qualifications, mentoring experience, expertise areas, and availability.
                </li>
                <li>
                  <span className="font-medium">Transactional information:</span> Payment details (processed securely via Stripe), transaction history, and billing information. We do not store payment card details on our systems.
                </li>
                <li>
                  <span className="font-medium">Usage data:</span> IP addresses, browser type, device identifiers, pages visited, and interaction data.
                </li>
                <li>
                  <span className="font-medium">Communications:</span> Messages exchanged between mentors and mentees, support requests, feedback, and other communications through our Services.
                </li>
                <li>
                  <span className="font-medium">Course and enrollment data:</span> Information about courses you enroll in or provide, including progress and completions.
                </li>
              </ul>
              <p className="text-gray-700">
                We do not collect sensitive information unless you voluntarily provide it and consent to its use.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">2. How we collect your information</h2>
              <p className="text-gray-700 mb-4">We may collect your personal information:</p>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
                <li>Directly from you when you register, update your profile, communicate with us, or use our Services.</li>
                <li>Automatically through your use of our Services, using cookies, logs, and similar technologies.</li>
                <li>From third parties, such as integrated payment processors (e.g., Stripe), analytics providers, or identity verification services.</li>
              </ul>
              <p className="text-gray-700">
                If we receive unsolicited personal information, we will handle it in accordance with APP 4, destroying or de-identifying it unless we are legally required to retain it.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">3. Why we collect your information</h2>
              <p className="text-gray-700 mb-4">We collect, hold, use, and disclose personal information to:</p>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
                <li>Provide, operate, and maintain our Services.</li>
                <li>Facilitate connections between mentors and mentees, including managing requests, approvals, and communications.</li>
                <li>Process payments and manage financial transactions securely.</li>
                <li>Improve and personalise your user experience.</li>
                <li>Respond to inquiries, support requests, and complaints.</li>
                <li>Conduct research and analytics to improve our Services.</li>
                <li>Prevent fraud, enhance security, and enforce our Terms of Service.</li>
                <li>Comply with legal and regulatory obligations.</li>
              </ul>
              <p className="text-gray-700">
                Due to the nature of our Services, anonymity or pseudonymity is generally not practicable (APP 2).
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">4. How we share your information</h2>
              <p className="text-gray-700 mb-4">We may disclose your personal information to:</p>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
                <li>Other users on the platform (e.g., mentors may view mentee profiles and vice versa). We do not disclose personal contact details unless you choose to share them.</li>
                <li>Service providers, including cloud hosting (e.g., Supabase), analytics providers, email service providers, and payment processors (e.g., Stripe).</li>
                <li>Professional advisers, such as legal, accounting, or technical consultants.</li>
                <li>Regulatory authorities or law enforcement, when required by law or to protect our rights.</li>
              </ul>
              <p className="text-gray-700">
                We do not sell your personal information to third parties. We only share the minimum amount of information necessary for the relevant purpose.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">5. Overseas disclosure</h2>
              <p className="text-gray-700 mb-4">
                Some of our third-party service providers may be located overseas, including but not limited to the United States and the European Economic Area (EEA).
              </p>
              <p className="text-gray-700">
                We take reasonable steps to ensure these parties comply with the APPs and this Privacy Policy when handling your personal information (APP 8).
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">6. Use of government identifiers</h2>
              <p className="text-gray-700">
                We do not adopt, use, or disclose government-related identifiers as our own identifiers of individuals (APP 9).
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">7. Security of your information</h2>
              <p className="text-gray-700">
                We implement appropriate technical, administrative, and organisational safeguards to protect your personal information. While we strive to use industry-standard measures to secure your data, no method of transmission or storage over the internet can be guaranteed as completely secure.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">8. Data retention</h2>
              <p className="text-gray-700 mb-4">
                We retain your personal information only for as long as necessary to fulfil the purposes for which it was collected or to comply with legal, accounting, or reporting requirements.
              </p>
              <p className="text-gray-700 mb-4">
                Inactive accounts are retained for up to 24 months before permanent deletion. Certain financial and transactional records may be retained for up to 7 years to comply with legal and accounting obligations.
              </p>
              <p className="text-gray-700">
                You may also request deletion of your personal information, subject to exceptions under law.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">9. Access and correction</h2>
              <p className="text-gray-700">
                You have the right to request access to, or correction of, your personal information held by us. To do so, please contact us using the details below.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">10. Your choices</h2>
              <p className="text-gray-700 mb-4">You have the following options regarding your personal information:</p>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
                <li>
                  <span className="font-medium">Account information:</span> You may update or correct certain details at any time via your profile settings.
                </li>
                <li>
                  <span className="font-medium">Account deletion:</span> You may request permanent deletion of your account.
                </li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">11. Children's privacy</h2>
              <p className="text-gray-700">
                Our Services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children without parental or guardian consent.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">12. Changes to this Privacy Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy from time to time. Material changes will be notified by posting the updated version on our website and updating the "Last updated" date above.
              </p>
              <p className="text-gray-700">
                We encourage you to review this Privacy Policy periodically.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-4">13. Contact us</h2>
              <p className="text-gray-700">
                If you have any questions, concerns, or complaints about this Privacy Policy or our data practices, please contact us at:
              </p>
              <p className="text-gray-700 mt-2">
                Email: support@emumentoring.com
              </p>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
